// Manages path-trace mode state and provides upstream BFS traversal for the BPMN viewer.
// Used by the layer manager (hover-driven dim) and as a data source for event-sourcing work.
import { ref, toRaw } from "vue";
import type { Ref } from "vue";

import type { ProcessElement } from "@/stores/process-elements-store";

// ── Primitive graph types ──────────────────────────────────────────────────────
// Plain interfaces rather than bpmn-js types so collectUpstream is unit-testable
// with fake objects without importing the full bpmn-js model system.

export interface TraversableConnection {
    id: string;
    type: string;
    source: TraversableElement;
}

export interface TraversableElement {
    id: string;
    incoming?: TraversableConnection[];
}

// ── Module-level toggle state ──────────────────────────────────────────────────

export const pathTraceActive = ref(false);

export const setPathTraceActive = (active: boolean): void => {
    pathTraceActive.value = active;
};

export const hoveredUpstreamTaskId = ref<string | null>(null);

/**
 * Deactivates path-trace mode and removes the hover dim state.
 * Call when entering edit mode or when mutually-exclusive focus modes activate.
 */
export const clearPathTrace = (): void => {
    pathTraceActive.value = false;
    hoveredUpstreamTaskId.value = null;
};

// ── Pure BFS traversal ─────────────────────────────────────────────────────────

/**
 * Backward BFS over the bpmn-js in-memory graph starting from `element`,
 * following sequence flows only (message flows are excluded).
 * Returns the set of all reachable predecessor element ids (including the
 * starting element itself) and the traversed sequence-flow connection ids.
 * A visited set prevents infinite loops in cyclic graphs.
 *
 * toRaw() is applied to unwrap Vue reactive proxies — bpmn-js defines `incoming`
 * as non-configurable on moddle objects, which causes proxy invariant errors
 * when a reactive-wrapped element is accessed. toRaw() is a no-op for plain objects.
 */
export const collectUpstream = (
    element: TraversableElement
): { elementIds: Set<string>; connectionIds: Set<string> } => {
    const raw = toRaw(element);
    const elementIds = new Set<string>([raw.id]);
    const connectionIds = new Set<string>();
    const visited = new Set<string>([raw.id]);
    const queue: TraversableElement[] = [raw];

    while (queue.length > 0) {
        const current = queue.shift()!;
        for (const conn of current.incoming ?? []) {
            if (conn.type !== "bpmn:SequenceFlow") continue;
            connectionIds.add(conn.id);
            const source = toRaw(conn.source);
            if (!visited.has(source.id)) {
                visited.add(source.id);
                elementIds.add(source.id);
                queue.push(source);
            }
        }
    }

    return { elementIds, connectionIds };
};

// ── Data-enrichment composable ─────────────────────────────────────────────────

type ElementRegistry = {
    get(
        id: string
    ):
        | { businessObject?: { processElementId?: string; id?: string } }
        | undefined;
};

/**
 * Composable that enriches upstream path data with domain ProcessElement info.
 * Returns getUpstreamData(element) which computes predecessor elements together
 * with their associated ProcessElements and entity refs — the data needed by
 * future event-sourcing work to reason about state available before a task runs.
 */
export const useUpstreamPaths = (
    elementRegistry: ElementRegistry | null | undefined,
    processElements: Ref<ProcessElement[] | undefined>
) => {
    const getUpstreamData = (element: TraversableElement) => {
        const { elementIds, connectionIds } = collectUpstream(element);

        const predecessorMap = new Map<string, ProcessElement>();
        for (const id of elementIds) {
            const el = elementRegistry?.get(id);
            if (!el?.businessObject) continue;
            const bo = el.businessObject;
            const pe = processElements.value?.find(
                (p) => p.identity === bo.processElementId || p.bpmn_id === bo.id
            );
            if (pe) predecessorMap.set(id, pe);
        }

        const entityRefs = [...predecessorMap.values()].flatMap(
            (pe) => pe.entity_refs ?? []
        );

        return { elementIds, connectionIds, predecessorMap, entityRefs };
    };

    return { getUpstreamData };
};
